import { Flex } from '@shared/components';

import { BasePAnalysisList, type PAnalysisListItem } from '../../components';
import {
  DEFAULT_EASY_THRESHOLD,
  DEFAULT_HARD_THRESHOLD,
  DEFAULT_MEDIUM_THRESHOLD,
} from '../../constants';
import { useItemAnalysisSearchParams } from '../../hooks';
import { useItemAnalysis } from '../../providers';
import { ItemAnalysisDifficultyMatrixGroup } from '../item-analysis-difficulty-matrix-group';
import { PAnalysisSection } from '../p-analysis-section';
import { PBufferConditionSection } from '../p-buffer-condition-section';

import {
  renderDifficultyLevelCell,
  renderIndicativeCell,
  type RenderIndicativeCellParams,
} from './utils';

export default function PAnalysisList() {
  const { searchItemAnalysisLoading, isFilterSet, questionAnalysisData } = useItemAnalysis();
  const [
    { page, pageSize, pBufferHardThreshold, pBufferMediumThreshold, pBufferEasyThreshold },
    { changePage, changePageSize },
  ] = useItemAnalysisSearchParams();

  const rows: PAnalysisListItem[] =
    questionAnalysisData?.records.map((record, index) => ({
      id: record.questionId,
      order: index + 1 + page * pageSize,
      code: record.questionRefCode,
      pInitial: renderDifficultyLevelCell(
        record.pInitial,
        DEFAULT_HARD_THRESHOLD,
        DEFAULT_MEDIUM_THRESHOLD,
        DEFAULT_EASY_THRESHOLD,
      ),
      pUse: renderDifficultyLevelCell(
        record.pUse,
        DEFAULT_HARD_THRESHOLD,
        DEFAULT_MEDIUM_THRESHOLD,
        DEFAULT_EASY_THRESHOLD,
      ),
      pCalculated: renderDifficultyLevelCell(
        record.pCalculated,
        DEFAULT_HARD_THRESHOLD,
        DEFAULT_MEDIUM_THRESHOLD,
        DEFAULT_EASY_THRESHOLD,
      ),
      pBuffer: renderDifficultyLevelCell(
        record.pCalculated,
        pBufferHardThreshold,
        pBufferMediumThreshold,
        pBufferEasyThreshold,
      ),
      status: record.status,
      bIndex: record.bIndexInitial == null ? null : Number(record.bIndexInitial.toFixed(2)),
      indicative: renderIndicativeCell({
        pUse: record.pUse,
        pBuffer: record.pCalculated,
        pBufferHardThreshold: DEFAULT_HARD_THRESHOLD,
        pBufferMediumThreshold: DEFAULT_MEDIUM_THRESHOLD,
        pBufferEasyThreshold: DEFAULT_EASY_THRESHOLD,
        pBufferSettingHardThreshold: pBufferHardThreshold,
        pBufferSettingMediumThreshold: pBufferMediumThreshold,
        pBufferSettingEasyThreshold: pBufferEasyThreshold,
      } as RenderIndicativeCellParams),
    })) ?? [];

  const total = questionAnalysisData?.total ?? 0;

  return (
    <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(6) }}>
      <PBufferConditionSection />
      <PAnalysisSection />
      <ItemAnalysisDifficultyMatrixGroup />
      <BasePAnalysisList
        isSearching={isFilterSet}
        rows={rows}
        loading={searchItemAnalysisLoading}
        slotProps={{
          footer: {
            total: total,
            pagination: {
              page: page,
              count: total,
              rowsPerPage: pageSize,
              onPageChange: (_event, page) => {
                changePage(page);
              },
              onRowsPerPageChange: (event) => {
                const pageSize = Number.parseInt(event.target.value, 10);
                changePageSize(pageSize);
              },
            },
          },
        }}
      />
    </Flex>
  );
}
